"use client";

import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export type BackgroundVariant = 'white' | 'slate-50' | 'slate-900' | 'nb-green' | 'glass-white' | 'glass-dark' | 'default' | 'muted' | 'primary';

interface SectionProps {
    children: React.ReactNode;
    variant?: BackgroundVariant;
    backgroundVariant?: BackgroundVariant;
    paddingTop?: string;
    paddingBottom?: string;
    backgroundColor?: string;
    isGlass?: boolean;
    blur?: string;
    id?: string;
    className?: string;
    noContainer?: boolean;
    animation?: 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right' | 'none';
    shadowIntensity?: string;
    borderWidth?: string;
    borderColor?: string;
    borderRadius?: string;
    marginTop?: string;
    marginBottom?: string;
    backgroundImage?: string;
    backgroundSize?: 'cover' | 'contain' | 'auto';
    backgroundPosition?: string;
    backgroundRepeat?: string;
    overlayColor?: string;
    overlayOpacity?: string;
    containerWidth?: 'narrow' | 'normal' | 'wide' | 'full';
    dataBlock?: string;
}

const getBgStyles = (variant: BackgroundVariant) => {
    switch (variant) {
        case 'slate-50':
        case 'muted': return 'bg-slate-50 text-slate-900';
        case 'slate-900': return 'bg-slate-900 text-white';
        case 'nb-green':
        case 'primary': return 'bg-nb-green text-white';
        case 'glass-white': return 'bg-white/70 backdrop-blur-xl border-y border-white/20';
        case 'glass-dark': return 'bg-slate-900/80 backdrop-blur-xl border-y border-white/5';
        case 'white':
        case 'default':
        default: return 'bg-white text-slate-900';
    }
};

const getPaddingValue = (val: string, side: 't' | 'b') => {
    const map: Record<string, string> = {
        "32": side === 't' ? "pt-8 md:pt-16" : "pb-8 md:pb-16",
        "24": side === 't' ? "pt-6 md:pt-12" : "pb-6 md:pb-12",
        "16": side === 't' ? "pt-4 md:pt-8" : "pb-4 md:pb-8",
        "12": side === 't' ? "pt-3 md:pt-6" : "pb-3 md:pb-6",
        "8": side === 't' ? "pt-2 md:pt-4" : "pb-2 md:pb-4",
        "4": side === 't' ? "pt-1 md:pt-2" : "pb-1 md:pb-2",
        "0": side === 't' ? "pt-0" : "pb-0"
    };
    return map[val] || (side === 't' ? "pt-4 md:pt-8" : "pb-4 md:pb-8");
};

export const Section = ({
    children,
    variant = 'white',
    backgroundVariant,
    paddingTop = "0",
    paddingBottom = "0",
    backgroundColor,
    isGlass = false,
    blur = "0",
    id,
    className = "",
    noContainer = false,
    animation = "none",
    shadowIntensity = "none",
    borderWidth = "0",
    borderColor = "border-slate-200",
    borderRadius = "",
    marginTop = "0",
    marginBottom = "0",
    backgroundImage,
    backgroundSize = 'cover',
    backgroundPosition = 'center',
    backgroundRepeat = 'no-repeat',
    overlayColor,
    overlayOpacity = "0.4",
    containerWidth = 'normal',
    dataBlock,
}: SectionProps) => {
    const pt = getPaddingValue(paddingTop, 't');
    const pb = getPaddingValue(paddingBottom, 'b');



    const blurClass = ({
        "0": "backdrop-blur-md",
        "blur-sm": "backdrop-blur-sm",
        "blur-md": "backdrop-blur-md",
        "blur-lg": "backdrop-blur-lg",
        "blur-xl": "backdrop-blur-xl",
    } as Record<string, string>)[blur] || "backdrop-blur-md";
    const glassClasses = isGlass ? `${blurClass} border-white/20 border-y` : "";

    const animationVariants = {
        "fade-up": { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 } },
        "fade-in": { initial: { opacity: 0 }, whileInView: { opacity: 1 } },
        "slide-left": { initial: { opacity: 0, x: -20 }, whileInView: { opacity: 1, x: 0 } },
        "slide-right": { initial: { opacity: 0, x: 20 }, whileInView: { opacity: 1, x: 0 } },
        "none": { initial: {}, whileInView: {} }
    };

    const anim = animationVariants[animation as keyof typeof animationVariants] || animationVariants.none;

    const getMargin = (val: string, type: 't' | 'b') => {
        const map: Record<string, string> = type === 't'
            ? { "32": "mt-32", "24": "mt-24", "16": "mt-16", "12": "mt-12", "8": "mt-8", "4": "mt-4", "0": "" }
            : { "32": "mb-32", "24": "mb-24", "16": "mb-16", "12": "mb-12", "8": "mb-8", "4": "mb-4", "0": "" };
        return map[val] || "";
    };

    const mt = getMargin(marginTop, 't');
    const mb = getMargin(marginBottom, 'b');
    const shadowClass = shadowIntensity !== "none" ? shadowIntensity : "";
    const borderWidthClass = ({ "1": "border", "2": "border-2", "4": "border-4", "8": "border-8" } as Record<string, string>)[borderWidth] || "";
    const customBorderColor = /^(#[0-9a-f]{3,8}|rgba?\(|hsla?\()/i.test(borderColor);
    const borderClass = borderWidthClass ? `${borderWidthClass} ${customBorderColor ? "" : borderColor}` : "";

    const containerClasses = {
        narrow: "max-w-3xl",
        normal: "container",
        wide: "max-w-7xl",
        full: "max-w-none px-0"
    }[containerWidth] || "container";

    const generatedId = React.useId();
    // Stabilize the ID fallback to avoid hydration mismatches if possible
    const uniqueSectionId = id || `section-${generatedId.replace(/:/g, '')}`;
    const safeBackgroundImage = backgroundImage?.replace(/["'\\\n\r]/g, "");
    const sectionStyle: React.CSSProperties = {
        backgroundColor: backgroundColor || undefined,
        backgroundImage: safeBackgroundImage ? `url("${safeBackgroundImage}")` : undefined,
        backgroundSize,
        backgroundPosition,
        backgroundRepeat,
        borderColor: customBorderColor && borderWidthClass ? borderColor : undefined,
    };

    return (
        <section
            id={uniqueSectionId}
            data-block={dataBlock}
            style={sectionStyle}
            className={cn(
                "relative overflow-hidden",
                !backgroundColor && !backgroundImage ? getBgStyles(backgroundVariant || variant) : "",
                pt, pb, mt, mb, shadowClass, borderClass, borderRadius, glassClasses,
                className
            )}
        >
            {/* Background Overlay */}
            {overlayColor && (
                <div
                    className="absolute inset-0 z-0 pointer-events-none section-overlay"
                    style={{ backgroundColor: overlayColor, opacity: Number(overlayOpacity) || 0 }}
                />
            )}

            {noContainer ? (
                <motion.div
                    initial={anim.initial}
                    whileInView={anim.whileInView}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                >
                    {children}
                </motion.div>
            ) : (
                <div className={`${containerClasses} mx-auto px-6 relative z-10`}>
                    <motion.div
                        initial={anim.initial}
                        whileInView={anim.whileInView}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        {children}
                    </motion.div>
                </div>
            )}
        </section>
    );
};
